import React from "react";
import { clsx } from "clsx";
import { OutlinedButton } from "@bloomberg/media-ui-core";
import typographyStyles from "@bloomberg/media-ui-tokens/css/typography.module.css";

import { CONTENT_TYPE } from "~constants";
import { getOpinionAuthorsText } from "~components/shared/brow/Brow";
import { convertMsToMinSecString } from "~components/shared/card/AudioVideoCard";
import Headline from "~components/shared/headline/Headline";
import {
    type MediaConfig,
    MemoizedMedia,
    MediaFormat,
    sourceHasValidMedia,
} from "~components/shared/media";
import OptionalEyebrow, {
    AUTHOR_EYEBROW,
} from "~components/shared/optional-eyebrow/OptionalEyebrow";
import Summary from "~components/shared/summary/Summary";
import { TranslatedMessage } from "~i18n/translate";
import { isOpinionItem } from "~models/transforms/eyebrow";
import { HEADLINE_SIZE, type PaginationProps } from "~types/lineup";
import {
    type CuratedStory,
    type VariationComponent,
    getAudio,
    getAudioDuration,
    getIsOpinionPageTheme,
    getVideo,
    getVideoDuration,
    isCuratedStory,
    ZoneColumnCombo,
} from "~types/types";
import getRelativeTime, { DEFAULT_TIME } from "~utils/getRelativeTime";
import { getOpinionBybrowPrefix } from "~utils/opinionBybrow";

import styles from "./styles.module.css";

type ContentArchiveProps = Partial<
    Pick<VariationComponent, "theme" | "renderingLocation">
> &
    Pick<VariationComponent, "items"> &
    PaginationProps;

const MAX_TIME_FOR_RED_ALERT = 30;
const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.videoImage,
    MediaFormat.defaultImage,
];
const MEDIA_CONFIG: MediaConfig = {
    aspectRatio: 3 / 2,
    className: styles.itemMedia,
    desktopMaxWidth: 195,
    mobileMaxWidth: 103,
    showCaption: false,
    hideVideoDuration: true,
};

const ARCHIVE_LABELS: { [key: string]: string | undefined } = {
    [CONTENT_TYPE.video]: "watch",
    [CONTENT_TYPE.audio]: "listen",
    opinion: "opinionRelated",
};

export const getPresetLabel = (
    item: CuratedStory,
    isOpinionStory: boolean,
    theme?: string,
    authorName?: string
) => {
    if (getIsOpinionPageTheme(theme) && authorName) {
        return AUTHOR_EYEBROW;
    }
    return isOpinionStory ? ARCHIVE_LABELS.opinion : ARCHIVE_LABELS[item.type];
};

const getDateString = (inputDateString: string) => {
    return new Date(inputDateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
};

const getTimeStampFromItem = (item: CuratedStory) => {
    const timeStampObj = item.publishedAt
        ? getRelativeTime(item.publishedAt)
        : { ...DEFAULT_TIME };

    const timeStampClasses = clsx(
        styles.itemTimestamp,
        typographyStyles.articleLabel,
        {
            [styles.recentTime]:
                timeStampObj?.time &&
                timeStampObj?.time < MAX_TIME_FOR_RED_ALERT &&
                timeStampObj?.label === "min",
        }
    );

    const timeStamp =
        timeStampObj.label === "hr" &&
        timeStampObj.time &&
        timeStampObj.time >= 24
            ? getDateString(item.publishedAt)
            : `${timeStampObj.time} ${timeStampObj.label} ago`;

    return { timeStamp, timeStampClasses };
};

const getTextLabel = (item: CuratedStory) => {
    if (item.type !== CONTENT_TYPE.video && item.type !== CONTENT_TYPE.audio) {
        return undefined;
    }
    const video = getVideo(item);
    const audio = getAudio(item);
    const duration = getVideoDuration(video) || getAudioDuration(audio);

    return duration ? convertMsToMinSecString(duration) : undefined;
};

export const ContentArchive = ({
    items,
    onRequestPaginate,
    paginationAvailable,
    paginationLoading,
    renderingLocation,
    theme,
}: ContentArchiveProps) => {
    return (
        <>
            {items.map((item) => {
                if (!isCuratedStory(item)) {
                    return null;
                }

                const { timeStamp, timeStampClasses } =
                    getTimeStampFromItem(item);

                const isOpinionStory = isOpinionItem(item);

                const textLabel = !isOpinionStory
                    ? getTextLabel(item)
                    : undefined;

                const opinionAuthor = getOpinionAuthorsText(item.credits);
                const opinionRole = getOpinionBybrowPrefix(item.credits);

                const presetLabel = getPresetLabel(
                    item,
                    isOpinionStory,
                    theme,
                    opinionAuthor
                );

                const containerClasses = clsx(styles.itemContainer, {
                    [styles.fullWidthItemContainer]:
                        renderingLocation ===
                        ZoneColumnCombo.fullWidthContentWell,
                });

                return (
                    <div key={item.url}>
                        <div className={containerClasses}>
                            <div className={styles.desktopTimestamp}>
                                <div className={timeStampClasses}>
                                    <time suppressHydrationWarning>
                                        {timeStamp}
                                    </time>
                                </div>
                            </div>
                            <div className={styles.itemTextContainer}>
                                <div className={styles.mobileTimestamp}>
                                    <div className={timeStampClasses}>
                                        <time suppressHydrationWarning>
                                            {timeStamp}
                                        </time>
                                    </div>
                                </div>
                                <a className={styles.itemLink} href={item.url}>
                                    <OptionalEyebrow
                                        textLabel={textLabel}
                                        presetLabel={presetLabel}
                                        authorName={
                                            getIsOpinionPageTheme(theme)
                                                ? opinionAuthor
                                                : item.byline
                                        }
                                        authorRole={
                                            getIsOpinionPageTheme(theme)
                                                ? opinionRole
                                                : undefined
                                        }
                                    />
                                    <Headline
                                        text={item.headline}
                                        url={item.url}
                                        size={HEADLINE_SIZE.SMALL}
                                        isPhoenix
                                    />
                                    {item.summary && (
                                        <Summary
                                            className={clsx(
                                                styles.itemSummary,
                                                typographyStyles.typography_articleDek
                                            )}
                                            summary={item.summary}
                                            isPhoenix
                                        />
                                    )}
                                </a>
                            </div>
                            {sourceHasValidMedia(item, MEDIA_PRIORITY) ? (
                                <MemoizedMedia
                                    source={item}
                                    priority={MEDIA_PRIORITY}
                                    config={MEDIA_CONFIG}
                                />
                            ) : (
                                <div className={styles.itemMediaPlaceholder} />
                            )}
                        </div>
                    </div>
                );
            })}
            <div className={styles.paginationContainer}>
                {paginationAvailable && (
                    <OutlinedButton
                        title="more stories"
                        onClick={onRequestPaginate}
                        loading={paginationLoading}
                        className={styles.loadMore}
                        version="phoenix"
                    >
                        <TranslatedMessage
                            id="loadMore"
                            defaultMessage="Load more"
                        />
                    </OutlinedButton>
                )}
            </div>
        </>
    );
};
